import React, { useEffect, useState } from 'react';
import { Pagination, PageSize } from '@uhg-abyss/web/ui/Pagination';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { coveo } from 'src/tools/coveo';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Text } from '@uhg-abyss/web/ui/Text';
import { event } from '@uhg-abyss/web/tools/event';
import { FacetChips } from './FacetChips';
import {
  facetChangeEvent,
  resultsPerPageEvent,
  pagerNumberEvent,
} from '../../events';

const SpaceBetween = styled('div', {
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  '@media (max-width: 1160px)': {
    flexWrap: 'wrap',
  },
});

const LightBorderPageSize = styled(PageSize, {
  '&.abyss-pagination-page-size-select-input': {
    borderColor: '$gray4',
  },
});

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

const UnpaddedButton = styled(Button, {
  '&.abyss-button-root': {
    padding: 0,
  },
});

const FacetContainer = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '5px',
});

const CustomHeading = styled('h4', {
  fontFamily: 'UHCSans, Arial, sans-serif',
  fontWeight: 700,
  fontSize: '1.602rem',
  lineHeight: '2rem',
  color: '$primary1',
});

export const PagerTop = ({
  controller,
  searchTerm,
  headlessEngine,
  facets,
  isFacetSelected,
  setIsFacetSelected,
  selectedFacets,
  setSelectedFacets,
}) => {
  const [pager, setPager] = useState(controller.state);
  const pageSize = headlessEngine.state.pagination.numberOfResults;
  const resultsTotalCount = headlessEngine.state.pagination.totalCountFiltered;
  const pageSizeOptions = [10, 15, 20];

  useEffect(() => {
    controller.subscribe(() => setPager(controller.state));
  }, [controller]);

  const pageIndexNum = parseInt(pager.currentPage - 1, 10);
  const resultFrom = pageIndexNum * pageSize + 1;
  const resultTo = (pageIndexNum + 1) * pageSize;

  let resultsString =
    resultFrom < resultTo && resultTo < resultsTotalCount
      ? `Results ${resultFrom}-${resultTo} of ${resultsTotalCount.toLocaleString()} for ${searchTerm}`
      : `Results ${resultFrom}-${resultsTotalCount} of ${resultsTotalCount.toLocaleString()} for ${searchTerm}`;

  if (resultsTotalCount === 1 || resultsTotalCount === resultFrom) {
    resultsString = `Result ${resultFrom} of ${resultsTotalCount.toLocaleString()} for ${searchTerm}`;
  }
  if (resultsTotalCount === 0) {
    resultsString = `No Results found`;
  }

  const handleFacetDeselection = () => {
    setIsFacetSelected(false);
    facets.forEach((facet) => {
      facet.controller.deselectAll();
    });
    setSelectedFacets([]);
    event.setData(
      facetChangeEvent({
        searchFacetValues: [],
        searchEventTrigger: 'facetClearAll',
      })
    );
  };

  useEffect(() => {
    if (selectedFacets.length === 0) {
      setIsFacetSelected(false);
    }
  }, [selectedFacets]);

  return (
    <Container>
      <SpaceBetween>
        <CustomHeading>{resultsString}</CustomHeading>
        <Layout.Group>
          <LightBorderPageSize
            pageSizeOptions={pageSizeOptions}
            pageSize={headlessEngine.state.pagination.numberOfResults}
            setPageSize={(size) => {
              coveo.controllers.resultsPerPage(headlessEngine).set(size);
              event.setData(
                resultsPerPageEvent({
                  searchItemsPerPage: size,
                  searchEventTrigger: 'resultsPerPage',
                })
              );
            }}
          />
          <Pagination
            variant="compact"
            gotoPage={(index) => {
              event.setData(
                pagerNumberEvent({
                  searchPagination: index + 1,
                  searchEventTrigger: 'pagerNumber',
                })
              );
              controller.selectPage(index + 1);
            }}
            previousPage={() => {
              event.setData(
                pagerNumberEvent({
                  searchPagination: pager.currentPage - 1,
                  searchEventTrigger: 'pagerNumber',
                })
              );
              controller.previousPage();
            }}
            nextPage={() => {
              event.setData(
                pagerNumberEvent({
                  searchPagination: pager.currentPage + 1,
                  searchEventTrigger: 'pagerNumber',
                })
              );
              controller.nextPage();
            }}
            pageCount={pager.currentPages[pager.currentPages.length - 1] || 1}
            canNextPage={pager.hasNextPage}
            canPreviousPage={pager.hasPreviousPage}
            pageIndex={pager.currentPage - 1}
          />
        </Layout.Group>
      </SpaceBetween>
      <FacetContainer>
        <For each="facet" of={facets} index="index">
          <If
            condition={
              isFacetSelected && facet.controller.state.hasActiveValues
            }
          >
            <Text size={14}>{facet.filterTitle}:</Text>
          </If>
          <FacetChips
            key={index}
            controller={facet.controller}
            filterTitle={facet.filterTitle}
            setSelectedFacets={setSelectedFacets}
            selectedFacets={selectedFacets}
          />
        </For>
      </FacetContainer>
      <If condition={isFacetSelected && selectedFacets.length > 0}>
        <Layout.Group>
          <UnpaddedButton
            variant="tertiary"
            onClick={() => handleFacetDeselection()}
          >
            Clear all filters
          </UnpaddedButton>
        </Layout.Group>
      </If>
    </Container>
  );
};

PagerTop.propTypes = {
  controller: PropTypes.shape({
    state: PropTypes.shape({
      currentPage: PropTypes.number,
      currentPages: PropTypes.arrayOf(PropTypes.number),
      hasNextPage: PropTypes.bool,
      hasPreviousPage: PropTypes.bool,
    }),
    subscribe: PropTypes.func.isRequired,
    selectPage: PropTypes.func.isRequired,
    previousPage: PropTypes.func.isRequired,
    nextPage: PropTypes.func.isRequired,
  }),
  searchTerm: PropTypes.string,
  headlessEngine: PropTypes.shape({
    state: {
      pagination: {
        numberOfResults: PropTypes.number,
        totalCountFiltered: PropTypes.number,
      },
    },
  }).isRequired,
  facets: PropTypes.arrayOf(
    PropTypes.shape({
      controller: {
        clear: PropTypes.shape({}),
      },
    })
  ),
  isFacetSelected: PropTypes.bool.isRequired,
  setIsFacetSelected: PropTypes.func.isRequired,
  selectedFacets: PropTypes.arrayOf(PropTypes.shape({})),
  setSelectedFacets: PropTypes.func.isRequired,
};

PagerTop.defaultProps = {
  controller: {
    state: {
      currentPage: 0,
      currentPages: [],
      hasNextPage: false,
      hasPreviousPage: false,
    },
  },
  searchTerm: '',
  facets: [],
  selectedFacets: [],
};
